import { fmtDM, fmtDMY, fmtYMD, parseDateAny, ymdToDate } from "../../../../shared/utils/dateFormat";

export { fmtDM, fmtDMY, fmtYMD, parseDateAny, ymdToDate };

const ARS_FORMATTER = new Intl.NumberFormat("es-AR", {
  style: "currency",
  currency: "ARS",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export function parseMoneyValue(value) {
  if (value === "" || value == null) return null;
  let raw = String(value).replace(/\$/g, "").replace(/\s/g, "");
  if (!raw) return null;

  if (raw.includes(",")) {
    // Coma como separador decimal (formato argentino)
    raw = raw.replace(/\./g, "").replace(",", ".");
  } else if (raw.includes(".")) {
    // Si no hay coma, verificar si es formato puro de miles (ej: 5.644.664 o 12.345)
    if (/^-?\d{1,3}(\.\d{3})+$/.test(raw)) {
      raw = raw.replace(/\./g, "");
    } else {
      // Punto como separador decimal: eliminar puntos anteriores
      const lastDotIndex = raw.lastIndexOf(".");
      raw = raw.slice(0, lastDotIndex).replace(/\./g, "") + "." + raw.slice(lastDotIndex + 1);
    }
  }

  // Rechazar más de 2 decimales
  const decimalMatch = raw.match(/\.(\d+)$/);
  if (decimalMatch && decimalMatch[1].length > 2) return NaN;

  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : NaN;
}

export function removeAccents(str) {
  return String(str || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

const MAX_TREASURY_AMOUNT = 999999999999.99;
const MONEY_EXPRESSION_NUMBER_PATTERN =
  /^(?:\d+|\d+[.,]\d{1,2}|\d{1,3}(?:\.\d{3})+(?:,\d{1,2})?)$/;

function createAmountExpressionError(code, message) {
  const error = new Error(message);
  error.code = code;
  return error;
}

export function evaluateAmountExpression(expressionStr) {
  if (expressionStr === "" || expressionStr == null) {
    return {
      success: false,
      code: "EMPTY",
      error: "El monto no puede estar vacío.",
    };
  }

  const raw = String(expressionStr).trim();
  if (!raw) {
    return {
      success: false,
      code: "EMPTY",
      error: "El monto no puede estar vacío.",
    };
  }

  // Verify allowed characters: numbers, ., ,, +, -, *, /, (, ) and whitespace
  if (/[^0-9.,+*/()\s-]/.test(raw)) {
    return {
      success: false,
      code: "INVALID_CHARACTER",
      error: "La expresión contiene caracteres no permitidos.",
    };
  }

  // Tokenization
  const tokens = [];
  let i = 0;
  while (i < raw.length) {
    const ch = raw[i];
    if (/\s/.test(ch)) {
      i++;
      continue;
    }
    if (["+", "-", "*", "/", "(", ")"].includes(ch)) {
      tokens.push({ type: ch === "(" || ch === ")" ? "PAREN" : "OPERATOR", value: ch });
      i++;
      continue;
    }
    if (/[0-9.,]/.test(ch)) {
      let numStr = "";
      while (i < raw.length && /[0-9.,]/.test(raw[i])) {
        numStr += raw[i];
        i++;
      }
      if (!/\d/.test(numStr)) {
        return {
          success: false,
          code: "INVALID_NUMBER",
          error: "Uno de los números de la expresión no es válido.",
        };
      }
      if (!MONEY_EXPRESSION_NUMBER_PATTERN.test(numStr)) {
        return {
          success: false,
          code: "INVALID_NUMBER",
          error: "Uno de los números de la expresión no es válido.",
        };
      }
      const parsedVal = parseMoneyValue(numStr);
      if (parsedVal === null || Number.isNaN(parsedVal)) {
        return {
          success: false,
          code: "INVALID_NUMBER",
          error: "Uno de los números de la expresión no es válido o tiene más de 2 decimales.",
        };
      }
      tokens.push({ type: "NUMBER", value: parsedVal, raw: numStr });
      continue;
    }
    return {
      success: false,
      code: "INVALID_CHARACTER",
      error: "La expresión contiene caracteres no permitidos.",
    };
  }

  if (tokens.length === 0) {
    return {
      success: false,
      code: "EMPTY",
      error: "El monto no puede estar vacío.",
    };
  }

  // Recursive Descent Parsing
  let tokenIdx = 0;

  function currentToken() {
    return tokens[tokenIdx];
  }

  function consume() {
    const tok = tokens[tokenIdx];
    tokenIdx++;
    return tok;
  }

  function parseExpression() {
    let left = parseTerm();

    while (
      currentToken() &&
      currentToken().type === "OPERATOR" &&
      (currentToken().value === "+" || currentToken().value === "-")
    ) {
      const op = consume().value;
      const right = parseTerm();
      if (op === "+") {
        left = left + right;
      } else {
        left = left - right;
      }
    }
    return left;
  }

  function parseTerm() {
    let left = parseFactor();

    while (
      currentToken() &&
      currentToken().type === "OPERATOR" &&
      (currentToken().value === "*" || currentToken().value === "/")
    ) {
      const op = consume().value;
      const right = parseFactor();
      if (op === "*") {
        left = left * right;
      } else {
        if (right === 0) {
          throw createAmountExpressionError("DIV_BY_ZERO", "No se puede dividir por cero.");
        }
        left = left / right;
      }
    }
    return left;
  }

  function parseFactor() {
    const tok = currentToken();
    if (!tok) {
      throw createAmountExpressionError(
        "SYNTAX_ERROR",
        "Sintaxis matemática incompleta o incorrecta."
      );
    }

    if (tok.type === "NUMBER") {
      consume();
      return tok.value;
    }

    if (tok.type === "PAREN" && tok.value === "(") {
      consume(); // consume '('
      const val = parseExpression();
      const nextTok = currentToken();
      if (!nextTok || nextTok.type !== "PAREN" || nextTok.value !== ")") {
        throw createAmountExpressionError(
          "UNBALANCED_PARENTHESES",
          "Paréntesis no balanceados."
        );
      }
      consume(); // consume ')'
      return val;
    }

    if (tok.type === "PAREN" && tok.value === ")") {
      throw createAmountExpressionError(
        "UNBALANCED_PARENTHESES",
        "Paréntesis no balanceados."
      );
    }

    throw createAmountExpressionError(
      "SYNTAX_ERROR",
      "Sintaxis matemática incompleta o incorrecta."
    );
  }

  try {
    const result = parseExpression();

    if (tokenIdx < tokens.length) {
      const remaining = tokens[tokenIdx];
      if (remaining.type === "PAREN" && remaining.value === ")") {
        return {
          success: false,
          code: "UNBALANCED_PARENTHESES",
          error: "Paréntesis no balanceados.",
        };
      }
      return {
        success: false,
        code: "SYNTAX_ERROR",
        error: "Sintaxis matemática incompleta o incorrecta.",
      };
    }

    if (result === null || result === undefined || !Number.isFinite(result)) {
      return {
        success: false,
        code: "NON_FINITE",
        error: "El resultado no es un número finito.",
      };
    }

    const rounded = Math.round((result + Number.EPSILON) * 100) / 100;

    if (rounded < 0) {
      return {
        success: false,
        code: "ZERO_OR_NEGATIVE",
        error: "El monto no puede ser negativo.",
      };
    }

    if (rounded === 0) {
      return {
        success: false,
        code: "ZERO_OR_NEGATIVE",
        error: "El monto debe ser mayor a cero.",
      };
    }

    if (rounded > MAX_TREASURY_AMOUNT) {
      return {
        success: false,
        code: "MAX_EXCEEDED",
        error: "El monto supera el máximo permitido.",
      };
    }

    return {
      success: true,
      value: rounded,
      formatted: formatMoney(rounded),
    };
  } catch (err) {
    if (err && err.code && err.message) {
      return {
        success: false,
        code: err.code,
        error: err.message,
      };
    }
    return {
      success: false,
      code: "SYNTAX_ERROR",
      error: "Sintaxis matemática incompleta o incorrecta.",
    };
  }
}

export function parseMoney(value) {
  const direct = parseMoneyValue(value);
  if (direct !== null && !Number.isNaN(direct)) return direct;
  const evaluated = evaluateAmountExpression(value);
  if (evaluated.success) return evaluated.value;
  return 0;
}

export function formatMoney(value) {
  return ARS_FORMATTER.format(Number(value || 0));
}

export function amountInputValue(value) {
  if (value === "" || value == null) return "";
  return String(value).replace(".", ",");
}

export function resolveMoneyInput(previousValue, candidateValue) {
  const evaluated = evaluateAmountExpression(candidateValue);
  if (!evaluated.success) {
    return {
      accepted: false,
      value: previousValue,
      parsed: parseMoneyValue(previousValue),
      error: evaluated.error,
      code: evaluated.code,
    };
  }

  return { accepted: true, value: candidateValue, parsed: evaluated.value };
}

export function moneyInputsAreEqual(currentValue, persistedValue) {
  const current = parseMoneyValue(currentValue);
  const persisted = parseMoneyValue(persistedValue);

  if (Number.isNaN(current) || Number.isNaN(persisted)) {
    return String(currentValue ?? "") === String(persistedValue ?? "");
  }

  return current === persisted;
}

export function rowMatchesFilters(row, filters, allRows = []) {
  if (row.rowState === "empty") {
    return (
      !filters.movementType &&
      !filters.categoryId &&
      !filters.status &&
      !filters.accountId
    );
  }

  const isTransfer =
    row.movementType === "transfer" ||
    Boolean(row.transferId) ||
    Boolean(row.clientTransferId);

  if (isTransfer) {
    if (filters.categoryId) return false;
    if (filters.movementType && filters.movementType !== "transfer") return false;
    if (filters.status && row.status !== filters.status) return false;

    if (filters.accountId) {
      const transferKey = row.transferId || row.clientTransferId;
      if (transferKey && Array.isArray(allRows) && allRows.length > 0) {
        const pairMatches = allRows.some((r) => {
          const rKey = r.transferId || r.clientTransferId;
          return rKey === transferKey && String(r.accountId) === String(filters.accountId);
        });
        if (!pairMatches) return false;
      } else if (String(row.accountId) !== String(filters.accountId)) {
        return false;
      }
    }

    return true;
  }

  if (filters.movementType && row.movementType !== filters.movementType) return false;
  if (
    filters.categoryId === "__uncategorized__" &&
    !row.categoryMissing &&
    row.categoryId !== "" &&
    row.categoryId !== null &&
    row.categoryId !== undefined
  ) {
    return false;
  }
  if (
    filters.categoryId &&
    filters.categoryId !== "__uncategorized__" &&
    String(row.categoryId) !== String(filters.categoryId)
  ) {
    return false;
  }
  if (filters.status && row.status !== filters.status) return false;
  if (filters.accountId && String(row.accountId) !== String(filters.accountId)) return false;
  return true;
}

export function isMovementRowVisible({
  row,
  rowIndex,
  filters,
  editable,
  firstBlankRowIndex,
  sessionOverrideIds,
  allRows = [],
}) {
  if (editable && row.rowState === "empty") {
    return true;
  }

  const transferKey = row.transferId || row.clientTransferId;
  if (transferKey && sessionOverrideIds && allRows.length > 0) {
    const isPairOverridden = allRows.some(
      (r) => (r.transferId || r.clientTransferId) === transferKey && sessionOverrideIds.has(r.clientId)
    );
    if (isPairOverridden) return true;
  } else if (sessionOverrideIds?.has(row.clientId)) {
    return true;
  }

  return rowMatchesFilters(row, filters, allRows);
}

export function displayDateForCell(value, referenceYearOrDate = null) {
  const parsed = parseDateAny(value, referenceYearOrDate);
  return parsed ? fmtDM(ymdToDate(parsed)) : String(value || "");
}

export function resolveDateWithinPeriod(value, period, referenceDate = null) {
  const raw = String(value ?? "").trim();
  const dayMonthMatch = raw.match(/^(\d{1,2})[/.-](\d{1,2})$/);

  if (!dayMonthMatch) {
    return parseDateAny(value, referenceDate || period?.periodStart);
  }

  const periodStart = parseDateAny(period?.periodStart);
  const periodEnd = parseDateAny(period?.periodEnd);
  if (!periodStart || !periodEnd || periodStart > periodEnd) {
    return parseDateAny(value, referenceDate || period?.periodStart);
  }

  const day = String(dayMonthMatch[1]).padStart(2, "0");
  const month = String(dayMonthMatch[2]).padStart(2, "0");
  const startYear = Number(periodStart.slice(0, 4));
  const endYear = Number(periodEnd.slice(0, 4));
  const candidates = [];

  for (let year = startYear; year <= endYear; year += 1) {
    const candidate = parseDateAny(`${day}/${month}/${year}`);
    if (candidate && candidate >= periodStart && candidate <= periodEnd) {
      candidates.push(candidate);
    }
  }

  if (candidates.length <= 1) return candidates[0] || "";

  const reference = parseDateAny(referenceDate);
  const referenceYear = reference?.slice(0, 4);
  return candidates.find((candidate) => candidate.startsWith(`${referenceYear}-`)) || candidates[0];
}

export function calculateCajaTotals(rows = []) {
  const income = rows.reduce((acc, r) => acc + Number(r.income || 0), 0);
  const expense = rows.reduce((acc, r) => acc + Number(r.expense || 0), 0);
  return {
    income,
    expense,
    balance: income - expense,
  };
}

export function sortMovementRows(rows = []) {
  const pairMap = new Map();
  for (const r of rows) {
    const key = r.transferId || r.clientTransferId;
    if (key) {
      if (!pairMap.has(key) || r.transferDirection === "OUTBOUND") {
        pairMap.set(key, r);
      }
    }
  }

  const getSortMeta = (r) => {
    const key = r.transferId || r.clientTransferId;
    if (key) {
      const outbound = pairMap.get(key) || r;
      const date = parseDateAny(outbound.date) || outbound.date || "";
      const id = Number(outbound.id) || outbound.clientId || key;
      const isOutbound = r.transferDirection === "OUTBOUND" ? 0 : 1;
      return { key, date, id, isOutbound, isPair: true };
    }
    const date = parseDateAny(r.date) || r.date || "";
    const id = Number(r.id) || r.clientId || "";
    return { key: null, date, id, isOutbound: 0, isPair: false };
  };

  return [...rows].sort((a, b) => {
    const metaA = getSortMeta(a);
    const metaB = getSortMeta(b);

    if (metaA.date !== metaB.date) {
      return metaA.date.localeCompare(metaB.date);
    }

    if (metaA.isPair && metaB.isPair && metaA.key === metaB.key) {
      return metaA.isOutbound - metaB.isOutbound;
    }

    const idAStr = String(metaA.id);
    const idBStr = String(metaB.id);
    if (idAStr !== idBStr) {
      return idAStr.localeCompare(idBStr, undefined, { numeric: true });
    }

    return metaA.isOutbound - metaB.isOutbound;
  });
}
